import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Button, Row, Col, Badge, Spinner, Card, Image } from 'react-bootstrap';
import { GoogleMap, MarkerF, DirectionsService, DirectionsRenderer, InfoWindow } from '@react-google-maps/api';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import apiClient from '../../api';
import { MapPin, Navigation, Clock, CheckCircle, Flag, Activity, ShieldCheck, Copy, Check, AlertTriangle, RefreshCw } from 'lucide-react';
import { generateDefenseReport } from '../utils/DefenseReportPDF';
import html2canvas from 'html2canvas';

const mapContainerStyle = {
    width: '100%',
    height: '600px',
    borderRadius: '12px'
};

const DEFAULT_CENTER = { lat: -29.9027, lng: -71.2519 };

const isValidCoord = (lat, lng) => {
    const l = Number(lat);
    const g = Number(lng);
    // Lat/Lng must be finite and non-zero (to avoid Null Island/Ocean fallback)
    return Number.isFinite(l) && Number.isFinite(g) && l !== 0 && g !== 0;
};

const formatWaitingMinutes = (minutes) => {
    if (!minutes) return "0 Min";
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hrs > 0) {
        return mins > 0 ? `${hrs} Hrs ${mins} Min` : `${hrs} Hrs`;
    }
    return `${mins} Min`;
};

const TripAuditModal = ({ show, onHide, trip, isLoaded }) => {
    const [positions, setPositions] = useState([]);
    const [evidences, setEvidences] = useState([]); // NUEVO: Fotos de Lens
    const [logs, setLogs] = useState([]); // NUEVO: Logs del viaje
    const [map, setMap] = useState(null);
    const mapContainerRef = useRef(null); // Ref para sacar la foto al mapa
    const [directions, setDirections] = useState(null);
    const [selectedPoint, setSelectedPoint] = useState(null);
    const [selectedEvidence, setSelectedEvidence] = useState(null); // NUEVO: Foto seleccionada
    const [loading, setLoading] = useState(true);
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
    const [copied, setCopied] = useState(false);
    const [showMap, setShowMap] = useState(false);
    const refreshTimer = useRef(null);

    const fetchAuditData = useCallback(async () => {
        if (!trip?.id) return;
        try {
            const [posRes, evidenceRes, logsRes] = await Promise.all([
                apiClient.get(`transport/trips/${trip.id}/positions`),
                apiClient.get(`transport/trips/${trip.id}/evidence`),
                apiClient.get(`transport/trips/${trip.id}/logs`)
            ]);
            setPositions(posRes.data);
            setEvidences(evidenceRes.data);
            setLogs(logsRes.data || []);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching audit data:", error);
            setLoading(false);
        }
    }, [trip?.id]);

    useEffect(() => {
        if (show && trip?.id) {
            setLoading(true);
            fetchAuditData();

            // ALEJANDRO BUGFIX: Solo activar refresco si el viaje está ACTIVO.
            // Si ya terminó, no tiene sentido re-consultar ni mover el mapa.
            const isTripActive = !['completed', 'cancelled', 'no_show'].includes(trip?.status?.toLowerCase());

            if (isTripActive) {
                console.log("⏱️ [Audit] Viaje activo. Activando refresco cada 20s.");
                refreshTimer.current = setInterval(fetchAuditData, 20000);
            }
        } else {
            setPositions([]);
            setEvidences([]);
            setLogs([]);
            setDirections(null);
            setSelectedPoint(null);
            setSelectedEvidence(null);
            setCopied(false);
            setShowMap(false);
            if (refreshTimer.current) clearInterval(refreshTimer.current);
        }
        return () => { if (refreshTimer.current) clearInterval(refreshTimer.current); };
    }, [show, trip?.id, trip?.status, fetchAuditData]);

    const handleDownloadDefenseReport = async () => {
        setIsGeneratingPDF(true);
        try {
            let mapImage = null;

            // CAPTURAR EL MAPA (Foto del contenedor actual)
            if (mapContainerRef.current) {
                const canvas = await html2canvas(mapContainerRef.current, {
                    useCORS: true,
                    allowTaint: false,
                    scale: 2 // Mayor calidad
                });
                mapImage = canvas.toDataURL('image/png');
            }

            await generateDefenseReport(trip, evidences, positions, mapImage);
        } catch (error) {
            console.error("Error generating report:", error);
            alert("Error al generar el PDF. Asegúrate de que las imágenes se carguen correctamente.");
        } finally {
            setIsGeneratingPDF(false);
        }
    };

    const handleCopyDefenseText = () => {
        const os = trip.external_reference || trip.id;
        const date = format(new Date(trip.scheduled_time), "dd/MM/yyyy");
        const sched = format(new Date(trip.scheduled_time), "HH:mm");
        const arrived = trip.gps_arrived_time ? format(new Date(trip.gps_arrived_time), "HH:mm:ss") : "N/R";
        const start = trip.start_time ? format(new Date(trip.start_time), "HH:mm:ss") : "N/R";
        const end = trip.end_time ? format(new Date(trip.end_time), "HH:mm:ss") : "N/R";

        const text = `Estimados, adjunto informe de cumplimiento para la reserva O.S. ${os}.
Datos de ejecución certificados por GPS:
- Fecha: ${date}
- Hora Programada: ${sched} hrs
- Llegada al Origen: ${arrived} hrs
- Inicio de Viaje: ${start} hrs
- Término de Viaje: ${end} hrs

El vehículo se encontraba en posición en el horario indicado. Se adjunta reporte PDF con evidencias fotográficas georreferenciadas.`;

        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const onLoad = useCallback((mapInstance) => {
        setMap(mapInstance);
    }, []);

    const directionsCallback = useCallback((response) => {
        if (response !== null && response.status === 'OK' && !directions) {
            setDirections(response);
        }
    }, [directions]);

    useEffect(() => {
        if (map && (positions.length > 0 || trip)) {
            const bounds = new window.google.maps.LatLngBounds();
            let validPoints = 0;

            positions.forEach(p => {
                if (isValidCoord(p.lat, p.lng)) {
                    bounds.extend({ lat: Number(p.lat), lng: Number(p.lng) });
                    validPoints++;
                }
            });

            if (isValidCoord(trip?.pickup_lat, trip?.pickup_lng)) {
                bounds.extend({ lat: Number(trip.pickup_lat), lng: Number(trip.pickup_lng) });
                validPoints++;
            }
            if (isValidCoord(trip?.dropoff_lat, trip?.dropoff_lng)) {
                bounds.extend({ lat: Number(trip.dropoff_lat), lng: Number(trip.dropoff_lng) });
                validPoints++;
            }

            if (validPoints > 0) {
                map.fitBounds(bounds, { padding: 50 });
            }
        }
    }, [map, positions, trip]);

    const isBookingTrip = Boolean(
        trip?.trip_source === 'api' ||
        trip?.external_reference ||
        trip?.client?.name?.toLowerCase().includes('booking') ||
        trip?.client_name?.toLowerCase().includes('booking')
    );

    const [isCheckingBooking, setIsCheckingBooking] = useState(false);

    const handleCheckBookingStatus = async () => {
        if (!trip?.id) return;
        setIsCheckingBooking(true);
        try {
            const response = await apiClient.post(`/booking/v2/trips/${trip.id}/sync-status`);
            const data = response.data;
            if (data?.is_cancelled) {
                alert(`⚠️ ALERTA DE CANCELACIÓN: Esta reserva figura CANCELADA en Booking.com (${data.booking_status}). EmiGo actualizó el estado a '${data.current_local_status}'.`);
            } else {
                alert(`✅ Booking confirma que la reserva sigue ACTIVA (${data.booking_status || 'OK'}). Estado local: ${data.current_local_status}.`);
            }
            fetchAuditData();
        } catch (err) {
            console.error("Error sincronizando con Booking:", err);
            const errMsg = err.response?.data?.detail || err.message || "Error al conectar con Booking";
            alert(`❌ Error al consultar Booking: ${errMsg}`);
        } finally {
            setIsCheckingBooking(false);
        }
    };

    if (!trip) return null;

    const mapCenter = (Number.isFinite(Number(trip.pickup_lat)) && Number.isFinite(Number(trip.pickup_lng)))
        ? { lat: Number(trip.pickup_lat), lng: Number(trip.pickup_lng) }
        : DEFAULT_CENTER;

    return (
        <Modal show={show} onHide={onHide} size="xl" centered className="trip-audit-modal" backdrop="static">
            <Modal.Header closeButton className="bg-dark text-white border-0 py-2">
                <Modal.Title className="h5 fw-bold mb-0 d-flex align-items-center gap-2">
                    <Activity size={20} className="text-primary" />
                    Auditoría GPS: {trip.passenger_name}
                    <Badge bg="primary" className="ms-2 small">#{trip.id}</Badge>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="bg-light p-0">
                <Row className="g-0">
                    <Col lg={9} className="position-relative">
                        {loading && (
                            <div className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-white bg-opacity-75" style={{ zIndex: 1000 }}>
                                <Spinner animation="border" variant="primary" />
                            </div>
                        )}

                        <div className="bg-white" ref={mapContainerRef}>
                            {isLoaded ? (
                                !showMap ? (
                                    <div
                                        className="d-flex flex-column justify-content-center align-items-center"
                                        style={{ ...mapContainerStyle, backgroundColor: '#f8f9fa', cursor: 'pointer' }}
                                        onClick={() => setShowMap(true)}
                                    >
                                        <MapPin size={48} className="text-muted mb-3" />
                                        <div className="fs-5 fw-bold text-dark mb-1">Haz click aquí</div>
                                        <div className="text-muted">para ver la ruta en el mapa</div>
                                    </div>
                                ) : (
                                    <GoogleMap
                                        mapContainerStyle={mapContainerStyle}
                                        center={mapCenter}
                                        zoom={14}
                                        onLoad={onLoad}
                                        options={{
                                            streetViewControl: false,
                                            mapTypeControl: true,
                                            styles: [{ featureType: "poi", stylers: [{ visibility: "off" }] }]
                                        }}
                                    >
                                        {/* RUTA TEÓRICA (Camino Feliz - Azul) */}
                                        {!directions && Number.isFinite(Number(trip.pickup_lat)) && Number.isFinite(Number(trip.dropoff_lat)) && (
                                            <DirectionsService
                                                options={{
                                                    origin: { lat: Number(trip.pickup_lat), lng: Number(trip.pickup_lng) },
                                                    destination: { lat: Number(trip.dropoff_lat), lng: Number(trip.dropoff_lng) },
                                                    travelMode: 'DRIVING',
                                                    waypoints: trip.stops ? trip.stops.sort((a, b) => a.order - b.order).map(stop => ({
                                                        location: { lat: Number(stop.lat), lng: Number(stop.lng) },
                                                        stopover: true
                                                    })) : []
                                                }}
                                                callback={directionsCallback}
                                            />
                                        )}
                                        {directions && (
                                            <DirectionsRenderer
                                                directions={directions}
                                                options={{
                                                    polylineOptions: { strokeColor: "#0d6efd", strokeOpacity: 0.6, strokeWeight: 6 },
                                                    preserveViewport: true,
                                                    suppressMarkers: true
                                                }}
                                            />
                                        )}

                                        {/* POSICIÓN AL APRETAR "EN CAMINO" */}
                                        {isValidCoord(trip.gps_en_route_lat, trip.gps_en_route_lng) && (
                                            <MarkerF
                                                position={{ lat: Number(trip.gps_en_route_lat), lng: Number(trip.gps_en_route_lng) }}
                                                icon="https://maps.google.com/mapfiles/ms/icons/yellow-dot.png"
                                                title="Acción: En Camino"
                                                label={{
                                                    text: "▶ En Camino",
                                                    color: "#333",
                                                    fontSize: "11px",
                                                    fontWeight: "bold"
                                                }}
                                            />
                                        )}

                                        {/* POSICIÓN AL APRETAR "EN POSICIÓN" */}
                                        {isValidCoord(trip.gps_arrived_lat, trip.gps_arrived_lng) && (
                                            <MarkerF
                                                position={{ lat: Number(trip.gps_arrived_lat), lng: Number(trip.gps_arrived_lng) }}
                                                icon="https://maps.google.com/mapfiles/ms/icons/orange-dot.png"
                                                title="Acción: En Posición"
                                                label={{
                                                    text: "📍 En Posición",
                                                    color: "#333",
                                                    fontSize: "11px",
                                                    fontWeight: "bold"
                                                }}
                                            />
                                        )}

                                        {/* POSICIÓN AL APRETAR "PASAJERO A BORDO" */}
                                        {isValidCoord(trip.gps_on_board_lat, trip.gps_on_board_lng) && (
                                            <MarkerF
                                                position={{ lat: Number(trip.gps_on_board_lat), lng: Number(trip.gps_on_board_lng) }}
                                                icon="https://maps.google.com/mapfiles/ms/icons/red-dot.png"
                                                title="Acción: Pasajero a Bordo"
                                                label={{
                                                    text: "🚗 A Bordo",
                                                    color: "#333",
                                                    fontSize: "11px",
                                                    fontWeight: "bold"
                                                }}
                                            />
                                        )}

                                        {/* POSICIÓN AL APRETAR "COMPLETAR VIAJE" */}
                                        {isValidCoord(trip.gps_completed_lat, trip.gps_completed_lng) && (
                                            <MarkerF
                                                position={{ lat: Number(trip.gps_completed_lat), lng: Number(trip.gps_completed_lng) }}
                                                icon="https://maps.google.com/mapfiles/ms/icons/green-dot.png"
                                                title="Acción: Finalizado"
                                                label={{
                                                    text: "✔ Finalizado",
                                                    color: "#333",
                                                    fontSize: "11px",
                                                    fontWeight: "bold"
                                                }}
                                            />
                                        )}

                                        {/* PUNTOS INTERACTIVOS (Banderitas/Flechas direccionales por estado) */}
                                        {positions.map((p, idx) => {
                                            const pointTime = new Date(p.timestamp || p.created_at).getTime();
                                            const arrivedTime = trip.gps_arrived_time ? new Date(trip.gps_arrived_time).getTime() : null;
                                            const onboardTime = trip.start_time ? new Date(trip.start_time).getTime() : null;

                                            let color = '#ffc107'; // Default: En camino (Amarillo)
                                            if (onboardTime && pointTime >= onboardTime) {
                                                color = '#dc3545'; // On Board (Rojo)
                                            } else if (arrivedTime && pointTime >= arrivedTime) {
                                                color = '#17a2b8'; // Arrived / Esperando (Celeste)
                                            }

                                            // No dibujar el último punto aquí para no superponer con el marcador principal de MÓVIL
                                            if (idx === positions.length - 1) return null;

                                            return (
                                                <MarkerF
                                                    key={`pt-${p.id}`}
                                                    position={{ lat: Number(p.lat), lng: Number(p.lng) }}
                                                    onClick={() => setSelectedPoint(p)}
                                                    icon={{
                                                        path: window.google?.maps?.SymbolPath?.FORWARD_CLOSED_ARROW,
                                                        scale: 6,
                                                        fillColor: color,
                                                        fillOpacity: 1,
                                                        strokeColor: 'white',
                                                        strokeWeight: 2,
                                                        rotation: p.heading || 0
                                                    }}
                                                />
                                            );
                                        })}

                                        {/* ULTIMA POSICION CONOCIDA (Punto Azul / Móvil) */}
                                        {positions.length > 0 && (
                                            <MarkerF
                                                position={{ lat: Number(positions[positions.length - 1].lat), lng: Number(positions[positions.length - 1].lng) }}
                                                zIndex={100}
                                                icon={{
                                                    path: window.google?.maps?.SymbolPath?.CIRCLE,
                                                    scale: 8,
                                                    fillColor: '#0d6efd',
                                                    fillOpacity: 1,
                                                    strokeColor: '#ffffff',
                                                    strokeWeight: 2.5
                                                }}
                                                label={{
                                                    text: "MÓVIL",
                                                    color: "#0d6efd",
                                                    fontWeight: "bold",
                                                    fontSize: "9px",
                                                    className: "mt-4 bg-white px-1 rounded border shadow-sm fw-bold"
                                                }}
                                            />
                                        )}

                                        {/* MARCADORES DE EVIDENCIA (LENS) */}
                                        {evidences.map((ev) => (
                                            <MarkerF
                                                key={`ev-${ev.id}`}
                                                position={{ lat: Number(ev.lat), lng: Number(ev.lng) }}
                                                onClick={() => setSelectedEvidence(ev)}
                                                icon={{
                                                    url: 'https://cdn-icons-png.flaticon.com/512/685/685655.png', // Icono de cámara
                                                    scaledSize: new window.google.maps.Size(30, 30),
                                                    origin: new window.google.maps.Point(0, 0),
                                                    anchor: new window.google.maps.Point(15, 15)
                                                }}
                                            />
                                        ))}

                                        {selectedEvidence && (
                                            <InfoWindow
                                                position={{ lat: Number(selectedEvidence.lat), lng: Number(selectedEvidence.lng) }}
                                                onCloseClick={() => setSelectedEvidence(null)}
                                            >
                                                <div className="p-0" style={{ maxWidth: '200px' }}>
                                                    <Image src={selectedEvidence.url_publica} fluid className="rounded border shadow-sm mb-1" />
                                                    <div className="px-1 small fw-bold text-primary">📸 Captura Lens</div>
                                                    <div className="px-1 text-muted" style={{ fontSize: '9px' }}>{format(new Date(selectedEvidence.captured_at), "HH:mm:ss")}</div>
                                                </div>
                                            </InfoWindow>
                                        )}

                                        {selectedPoint && (
                                            <InfoWindow
                                                position={{ lat: Number(selectedPoint.lat), lng: Number(selectedPoint.lng) }}
                                                onCloseClick={() => setSelectedPoint(null)}
                                            >
                                                <div className="p-1" style={{ minWidth: '150px' }}>
                                                    <div className="fw-bold border-bottom pb-1 mb-2" style={{ fontSize: '12px' }}>
                                                        Fecha hora: {selectedPoint.timestamp || selectedPoint.created_at ? format(new Date(selectedPoint.timestamp || selectedPoint.created_at), "dd/MM/yyyy HH:mm") : 'N/A'}
                                                    </div>
                                                    <div className="small mb-1" style={{ fontSize: '11px' }}>
                                                        Velocidad: {Math.round(selectedPoint.speed || 0)} Km/h
                                                    </div>
                                                    <div className="d-flex align-items-center gap-2 small">
                                                        <MapPin size={14} className="text-muted" />
                                                        <span style={{ fontSize: '9px' }}>{Number(selectedPoint.lat).toFixed(5)}, {Number(selectedPoint.lng).toFixed(5)}</span>
                                                    </div>
                                                </div>
                                            </InfoWindow>
                                        )}

                                        {/* HITOS FÍSICOS */}
                                        {isValidCoord(trip.pickup_lat, trip.pickup_lng) && (
                                            <MarkerF
                                                position={{ lat: Number(trip.pickup_lat), lng: Number(trip.pickup_lng) }}
                                                icon={{ url: 'https://maps.google.com/mapfiles/ms/icons/green-dot.png' }}
                                                label={{ text: "RECOGIDA", color: "green", fontWeight: "bold", fontSize: "10px", className: "mt-4 bg-white px-1 rounded border" }}
                                            />
                                        )}

                                        {/* PARADAS INTERMEDIAS */}
                                        {trip.stops && trip.stops.map((stop, idx) => (
                                            isValidCoord(stop.lat, stop.lng) && (
                                                <MarkerF
                                                    key={`stop-marker-${stop.id || idx}`}
                                                    position={{ lat: Number(stop.lat), lng: Number(stop.lng) }}
                                                    icon={{ url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png' }}
                                                    label={{
                                                        text: `PARADA ${idx + 1}${stop.passenger_name ? `: ${stop.passenger_name}` : ''}`,
                                                        color: "#0d6efd",
                                                        fontWeight: "bold",
                                                        fontSize: "9px",
                                                        className: "mt-4 bg-white px-1 rounded border shadow-sm fw-bold"
                                                    }}
                                                />
                                            )
                                        ))}

                                        {isValidCoord(trip.dropoff_lat, trip.dropoff_lng) && (
                                            <MarkerF
                                                position={{ lat: Number(trip.dropoff_lat), lng: Number(trip.dropoff_lng) }}
                                                icon={{ url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png' }}
                                                label={{ text: "DESTINO", color: "red", fontWeight: "bold", fontSize: "10px", className: "mt-4 bg-white px-1 rounded border" }}
                                            />
                                        )}
                                    </GoogleMap>
                                )
                            ) : <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>}
                        </div>

                        {/* LEYENDA FLOTANTE */}
                        <div className="position-absolute bottom-0 start-0 m-3 p-2 bg-white bg-opacity-90 rounded shadow-sm border small" style={{ zIndex: 100 }}>
                            <div className="d-flex align-items-center gap-2 mb-1">
                                <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#ffc107' }}></div>
                                <span>Aproximación (En Camino)</span>
                            </div>
                            <div className="d-flex align-items-center gap-2 mb-1">
                                <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#17a2b8' }}></div>
                                <span>Llegó / Tiempo de Espera</span>
                            </div>
                            <div className="d-flex align-items-center gap-2 mb-1">
                                <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#dc3545' }}></div>
                                <span>Servicio (Pasajero a Bordo)</span>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <div style={{ width: '20px', height: '3px', backgroundColor: '#0d6efd', opacity: 0.6 }}></div>
                                <span>Ruta Teórica</span>
                            </div>
                        </div>
                    </Col>

                    <Col lg={3} className="bg-white border-start">
                        <div className="p-3 h-100 overflow-auto" style={{ maxHeight: '600px' }}>

                            {/* BOTONES DE DEFENSA */}
                            <div className="mb-4">
                                <Button
                                    variant="primary"
                                    className="w-100 mb-2 d-flex align-items-center justify-content-center gap-2 fw-bold shadow-sm"
                                    size="sm"
                                    onClick={handleDownloadDefenseReport}
                                    disabled={isGeneratingPDF}
                                >
                                    {isGeneratingPDF ? <Spinner animation="border" size="sm" /> : <ShieldCheck size={18} />}
                                    PDF DE DEFENSA (O.S.)
                                </Button>
                                <Button
                                    variant="outline-primary"
                                    className="w-100 d-flex align-items-center justify-content-center gap-2 fw-bold"
                                    size="sm"
                                    onClick={handleCopyDefenseText}
                                >
                                    {copied ? <Check size={18} className="text-success" /> : <Copy size={18} />}
                                    {copied ? "COPIADO" : "COPIAR TEXTO RÁPIDO"}
                                </Button>

                                {isBookingTrip && (
                                    <Button
                                        variant="outline-info"
                                        className="w-100 mt-2 d-flex align-items-center justify-content-center gap-2 fw-bold text-dark border-info shadow-sm"
                                        size="sm"
                                        onClick={handleCheckBookingStatus}
                                        disabled={isCheckingBooking}
                                        title="Consultar estado en vivo a la API oficial de Booking.com"
                                    >
                                        {isCheckingBooking ? <Spinner animation="border" size="sm" /> : <RefreshCw size={16} />}
                                        {isCheckingBooking ? "CONSULTANDO BOOKING..." : "VERIFICAR EN BOOKING"}
                                    </Button>
                                )}
                            </div>

                            <h6 className="fw-bold text-muted small mb-3 text-uppercase letter-spacing-1 border-bottom pb-2">Evidencias Visuales ({evidences.length})</h6>

                            {/* GALERÍA DE LENS */}
                            <div className="mb-4">
                                {evidences.length === 0 ? (
                                    <div className="p-2 bg-light rounded text-center small italic text-muted border border-dashed">Sin fotos tomadas.</div>
                                ) : (
                                    <Row className="g-2">
                                        {evidences.map(ev => (
                                            <Col xs={6} key={ev.id}>
                                                <Card className="border-0 shadow-sm overflow-hidden cursor-pointer h-100" onClick={() => setSelectedEvidence(ev)}>
                                                    <Card.Img variant="top" src={ev.url_publica} style={{ height: '60px', objectFit: 'cover' }} title="Click para ver en mapa" />
                                                    <div className="p-1 text-center" style={{ fontSize: '8px' }}>{format(new Date(ev.captured_at), "HH:mm")}</div>
                                                </Card>
                                            </Col>
                                        ))}
                                    </Row>
                                )}
                            </div>

                            <h6 className="fw-bold text-muted small mb-3 text-uppercase letter-spacing-1 border-bottom pb-2">Hitos del Tiempo</h6>

                            <div className="timeline-audit mt-2">
                                {trip.created_at && (
                                    <div className="d-flex gap-2 mb-3 align-items-start">
                                        <div className="bg-primary bg-opacity-10 p-2 rounded text-primary border border-primary border-opacity-25"><Clock size={16} /></div>
                                        <div className="small lh-sm">
                                            <div className="text-primary fw-bold" style={{ fontSize: '10px' }}>CREADO EL</div>
                                            <div className="fw-bold text-dark">{format(new Date(trip.created_at), "dd/MM/yyyy HH:mm 'hrs'", { locale: es })}</div>
                                        </div>
                                    </div>
                                )}

                                <div className="d-flex gap-2 mb-3 align-items-start opacity-75">
                                    <div className="bg-light p-2 rounded text-muted"><Clock size={16} /></div>
                                    <div className="small lh-sm">
                                        <div className="text-muted" style={{ fontSize: '10px' }}>PROGRAMADO</div>
                                        <div className="fw-bold">{format(new Date(trip.scheduled_time), "dd/MM/yyyy HH:mm 'hrs'", { locale: es })}</div>
                                    </div>
                                </div>

                                {trip.gps_en_route_time && (
                                    <div className="d-flex gap-2 mb-3 align-items-start">
                                        <div className="bg-warning bg-opacity-10 p-2 rounded text-warning border border-warning border-opacity-25"><Navigation size={16} /></div>
                                        <div className="small lh-sm">
                                            <div className="text-warning fw-bold" style={{ fontSize: '10px' }}>INICIÓ RUTA</div>
                                            <div className="fw-bold text-dark">{format(new Date(trip.gps_en_route_time), "HH:mm:ss")}</div>
                                        </div>
                                    </div>
                                )}

                                {trip.gps_arrived_time && (
                                    <div className="d-flex gap-2 mb-3 align-items-start">
                                        <div className="bg-warning bg-opacity-25 p-2 rounded text-dark border border-warning"><MapPin size={16} /></div>
                                        <div className="small lh-sm">
                                            <div className="text-muted fw-bold" style={{ fontSize: '10px' }}>LLEGÓ AL ORIGEN</div>
                                            <div className="fw-bold text-dark">{format(new Date(trip.gps_arrived_time), "HH:mm:ss")}</div>
                                        </div>
                                    </div>
                                )}

                                {(() => {
                                    const noShowLog = logs.find(log => 
                                        log.action?.toLowerCase() === 'actualizado' && 
                                        log.details?.toLowerCase().includes('no_show')
                                    );
                                    const noShowTimestamp = noShowLog ? noShowLog.timestamp : (trip.status?.toLowerCase() === 'no_show' ? trip.end_time : null);
                                    
                                    if (noShowTimestamp) {
                                        return (
                                            <div className="d-flex gap-2 mb-3 align-items-start">
                                                <div className="bg-danger bg-opacity-10 p-2 rounded text-danger border border-danger border-opacity-25"><AlertTriangle size={16} /></div>
                                                <div className="small lh-sm">
                                                    <div className="text-danger fw-bold" style={{ fontSize: '10px' }}>NO SHOW DECLARADO</div>
                                                    <div className="fw-bold text-dark">{format(new Date(noShowTimestamp), "HH:mm:ss")}</div>
                                                </div>
                                            </div>
                                        );
                                    }
                                    return null;
                                })()}

                                {trip.start_time && (
                                    <div className="d-flex gap-2 mb-3 align-items-start">
                                        <div className="bg-primary bg-opacity-10 p-2 rounded text-primary border border-primary border-opacity-25"><CheckCircle size={16} /></div>
                                        <div className="small lh-sm">
                                            <div className="text-primary fw-bold" style={{ fontSize: '10px' }}>PASAJERO A BORDO</div>
                                            <div className="fw-bold text-dark">{format(new Date(trip.start_time), "HH:mm:ss")}</div>
                                            {trip.waiting_time_minutes > 0 && (
                                                <Badge bg="warning" text="dark" className="mt-1" style={{ fontSize: '9px' }}>
                                                    ⏳ ESPERA: {formatWaitingMinutes(trip.waiting_time_minutes)}
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* PARADAS INTERMEDIAS */}
                                {trip.stops && trip.stops.length > 0 && trip.stops.sort((a, b) => a.order - b.order).map((stop, idx) => (
                                    <React.Fragment key={stop.id || idx}>
                                        {stop.arrived_at && (
                                            <div className="d-flex gap-2 mb-3 align-items-start">
                                                <div className="bg-info bg-opacity-10 p-2 rounded text-info border border-info border-opacity-25"><MapPin size={16} /></div>
                                                <div className="small lh-sm">
                                                    <div className="text-info fw-bold" style={{ fontSize: '10px' }}>LLEGÓ A PARADA {idx + 1}</div>
                                                    <div className="fw-bold text-dark">{format(new Date(stop.arrived_at), "HH:mm:ss")}</div>
                                                    <div className="text-muted text-truncate" style={{ fontSize: '9px', maxWidth: '150px' }}>{stop.address}</div>
                                                </div>
                                            </div>
                                        )}
                                        {stop.departed_at && (
                                            <div className="d-flex gap-2 mb-3 align-items-start">
                                                <div className="bg-secondary bg-opacity-10 p-2 rounded text-secondary border border-secondary border-opacity-25"><Navigation size={16} /></div>
                                                <div className="small lh-sm">
                                                    <div className="text-secondary fw-bold" style={{ fontSize: '10px' }}>CONTINUÓ VIAJE</div>
                                                    <div className="fw-bold text-dark">{format(new Date(stop.departed_at), "HH:mm:ss")}</div>
                                                </div>
                                            </div>
                                        )}
                                    </React.Fragment>
                                ))}

                                {trip.end_time && trip.status?.toLowerCase() !== 'no_show' && (
                                    <div className="d-flex gap-2 mb-3 align-items-start">
                                        <div className="bg-success bg-opacity-10 p-2 rounded text-success border border-success border-opacity-25"><Flag size={16} /></div>
                                        <div className="small lh-sm">
                                            <div className="text-success fw-bold" style={{ fontSize: '10px' }}>FINALIZÓ SERVICIO</div>
                                            <div className="fw-bold text-dark">{format(new Date(trip.end_time), "HH:mm:ss")}</div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="mt-4 p-3 bg-dark text-white rounded shadow-sm text-center">
                                <div className="small fw-bold opacity-75 mb-1" style={{ fontSize: '10px' }}>PRECISIÓN DE RUTA</div>
                                <div className="h4 fw-bold mb-0">{positions.length} <small className="fs-6 opacity-50">puntos</small></div>
                                <div className="small opacity-50 mt-1" style={{ fontSize: '9px' }}>Captura activa cada 90s</div>
                            </div>

                            <div className="mt-3 p-2 bg-light rounded small italic text-muted text-center" style={{ fontSize: '11px' }}>
                                El mapa se actualiza automáticamente cada 15 segundos.
                            </div>
                        </div>
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer className="bg-white border-top py-2">
                <Button variant="outline-secondary" size="sm" className="px-4" onClick={onHide}>Cerrar Auditoría</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default TripAuditModal;
